/**
 * Hub treasure chest cues. Brandt approved v4 plus padlock-1.
 * The files are already leveled (hold about -18 LUFS, open -15, cancel -20,
 * padlock about -16), so they play at unity on the chime bus.
 *
 * Hold is a hard 0.8s with no baked tail, so it can be cut. Open begins with a
 * rim latch about 80ms in, then the lid thunk. padlock-1's second tumbler sits
 * about 15ms before that latch and flams if the two clips start together.
 * Starting the padlock 40ms earlier seats the next tumbler on the latch and the
 * shackle pop on the thunk. The padlock still starts as the chest pops; the
 * open clip follows inside that 40ms, with no gap after the gesture.
 */

export const CHEST_HOLD_FILE = 'chest-v4-hold';
export const CHEST_OPEN_FILE = 'chest-v4-open';
export const CHEST_CANCEL_FILE = 'chest-v4-cancel';
export const CHEST_PADLOCK_FILE = 'padlock-1';

/** Prize Box reveal only. The collection chest still uses chest-v4-open. */
export const PRIZE_FANFARE_FILE = 'prize-fanfare-5';

export const CHEST_CLIPS = [CHEST_HOLD_FILE, CHEST_OPEN_FILE, CHEST_CANCEL_FILE, CHEST_PADLOCK_FILE] as const;

/** Matches the rendered hold. The chest pops when this elapses. */
export const CHEST_HOLD_MS = 800;

/** Early release fades the hold out. Short enough to feel immediate, long enough to avoid a click. */
export const CHEST_HOLD_FADE_SEC = 0.03;

/** How much earlier padlock-1 starts, relative to chest-v4-open. Never more than 40ms. */
export const PADLOCK_LEAD_SEC = 0.04;

export function chestAudible(muted: boolean, effectsOn: boolean): boolean {
  return !muted && effectsOn;
}

export interface ChestPlay {
  file: string;
  /** Seconds on the audio context clock. */
  at: number;
}

/** Padlock at `now`, open one lead later. The lead is the whole offset. */
export function planChestOpen(now: number, lead = PADLOCK_LEAD_SEC): ChestPlay[] {
  const clamped = Math.min(0.04, Math.max(0, lead));
  return [
    { file: CHEST_PADLOCK_FILE, at: now },
    { file: CHEST_OPEN_FILE, at: now + clamped },
  ];
}

interface ChestBuffer {
  duration: number;
}

interface ChestGain {
  value: number;
  cancelScheduledValues(when: number): void;
  setValueAtTime(value: number, when: number): void;
  linearRampToValueAtTime(value: number, when: number): void;
}

interface ChestSource {
  buffer: ChestBuffer | null;
  connect(node: unknown): void;
  start(when?: number): void;
  stop(when?: number): void;
}

interface ChestContext {
  currentTime: number;
  createBufferSource(): ChestSource;
  createGain(): { gain: ChestGain; connect(node: unknown): void };
}

/**
 * One hold at a time. Open and the padlock layer on top.
 * A late hold decode is dropped if the player already let go.
 */
export class ChestCue {
  private token = 0;
  private cancelToken = 0;
  private holding = false;
  private holdSrc: ChestSource | null = null;
  private holdGain: ChestGain | null = null;
  private fanfareSrc: ChestSource | null = null;
  private readonly layers: ChestSource[] = [];

  constructor(
    private readonly context: () => ChestContext | null,
    private readonly bus: () => unknown,
    private readonly silenced: () => boolean,
    private readonly load: (file: string) => Promise<ChestBuffer | null>,
  ) {}

  preload(): void {
    for (const file of CHEST_CLIPS) void this.load(file);
    void this.load(PRIZE_FANFARE_FILE);
  }

  hold(): void {
    this.token += 1;
    const token = this.token;
    this.holding = true;
    this.stopHold(0);
    if (this.silenced()) return;
    void this.load(CHEST_HOLD_FILE).then((buffer) => {
      if (!buffer || token !== this.token || !this.holding || this.silenced()) return;
      this.holdSrc = this.start(buffer, this.context()?.currentTime ?? 0, true);
    });
  }

  /** Early release. The hold stops at once and cancel plays. Open never starts. */
  cancel(): void {
    if (!this.holding) return;
    this.holding = false;
    this.token += 1;
    this.stopHold(CHEST_HOLD_FADE_SEC);
    this.playCancel();
  }

  /**
   * Cancel stinger on its own. A spin hold has no chest hold running, so
   * {@link cancel} would return without playing anything.
   */
  playCancel(): void {
    this.cancelToken += 1;
    const token = this.cancelToken;
    if (this.silenced()) return;
    void this.load(CHEST_CANCEL_FILE).then((buffer) => {
      if (!buffer || token !== this.cancelToken || this.silenced()) return;
      const src = this.start(buffer, this.context()?.currentTime ?? 0, false);
      if (src) this.layers.push(src);
    });
  }

  /**
   * The chest popped. Hold stops immediately. Padlock and open are scheduled
   * from the same clock read so the padlock leads by {@link PADLOCK_LEAD_SEC}.
   */
  open(): void {
    this.holding = false;
    this.token += 1;
    this.cancelToken += 1;
    const token = this.token;
    this.stopHold(0);
    this.stopFanfare();
    if (this.silenced()) return;
    const now = this.context()?.currentTime ?? 0;
    const plan = planChestOpen(now);
    void Promise.all(plan.map(async (item) => ({ item, buffer: await this.load(item.file) }))).then((rows) => {
      if (token !== this.token || this.silenced()) return;
      const live = this.context()?.currentTime ?? now;
      const slip = Math.max(0, live - now);
      for (const row of rows) {
        if (!row.buffer) continue;
        const src = this.start(row.buffer, row.item.at + slip, false);
        if (src) this.layers.push(src);
      }
    });
  }

  /**
   * Prize Box reveal, after the hold completes. The hold stops. The collection
   * chest pop and its padlock do not start, and a fanfare already playing is
   * cut so the reveal does not stack on itself or on the chest pop.
   */
  prizeOpen(): void {
    this.holding = false;
    this.token += 1;
    this.cancelToken += 1;
    const token = this.token;
    this.stopHold(0);
    this.stopLayers();
    this.stopFanfare();
    if (this.silenced()) return;
    const now = this.context()?.currentTime ?? 0;
    void this.load(PRIZE_FANFARE_FILE).then((buffer) => {
      if (!buffer || token !== this.token || this.silenced()) return;
      this.stopFanfare();
      const live = this.context()?.currentTime ?? now;
      const src = this.start(buffer, live, false);
      if (src) this.fanfareSrc = src;
    });
  }

  /** Mute, effects off, or leaving the hub. Stops the hold and any layered pop. */
  halt(): void {
    this.holding = false;
    this.token += 1;
    this.cancelToken += 1;
    this.stopHold(0);
    this.stopLayers();
    this.stopFanfare();
  }

  /**
   * Scene shutdown after a successful open. Drops a hold that is still waiting
   * to decode, and leaves the pop layers running.
   */
  dropHold(): void {
    if (!this.holding) {
      this.stopHold(0);
      return;
    }
    this.holding = false;
    this.token += 1;
    this.stopHold(0);
  }

  private stopFanfare(): void {
    const src = this.fanfareSrc;
    this.fanfareSrc = null;
    if (!src) return;
    try {
      src.stop();
    } catch {
      // Already finished.
    }
  }

  private stopLayers(): void {
    for (const src of this.layers) {
      try {
        src.stop();
      } catch {
        // Already finished.
      }
    }
    this.layers.length = 0;
  }

  private stopHold(fadeSec: number): void {
    const src = this.holdSrc;
    const amp = this.holdGain;
    this.holdSrc = null;
    this.holdGain = null;
    if (!src) return;
    const ctx = this.context();
    if (!ctx || !amp || fadeSec <= 0) {
      try {
        src.stop();
      } catch {
        // Already finished.
      }
      return;
    }
    const now = ctx.currentTime;
    try {
      amp.cancelScheduledValues(now);
      amp.setValueAtTime(amp.value, now);
      amp.linearRampToValueAtTime(0.0001, now + fadeSec);
      src.stop(now + fadeSec);
    } catch {
      try {
        src.stop();
      } catch {
        // Already finished.
      }
    }
  }

  private start(buffer: ChestBuffer, when: number, trackHold: boolean): ChestSource | null {
    const ctx = this.context();
    const dest = this.bus();
    if (!ctx || !dest) return null;
    const src = ctx.createBufferSource();
    const amp = ctx.createGain();
    amp.gain.setValueAtTime(1, ctx.currentTime);
    src.buffer = buffer;
    src.connect(amp);
    amp.connect(dest);
    try {
      src.start(when);
    } catch {
      return null;
    }
    if (trackHold) this.holdGain = amp.gain;
    return src;
  }
}
